import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks/useAuth";
import {
  listClubs,
  getClub,
  createClubLegacy,
  updateClub,
  listExpiredClubs,
  inactivateClub,
  reactivateClub,
  createStaffClubRegistration,
} from "../../services/clubService";
import { listMemberGenders, listMemberTitles } from "../../services/memberService";
import { listFees } from "../../services/feeService";
import { listClubRegistrationProcesses, getClubRegistrationProcess } from "../../services/registrationProcessService";
import {
  searchClubRegistrationPayment,
  confirmClubRegistrationPayment,
  getClubShroffVoucherPdf,
  listMyManualPaymentSubmissions,
  uploadManualPaymentReceipt,
  getManualPaymentReceiptFile,
  getClubPayments,
} from "../../services/paymentService";
import {
  TOKENS,
  Icon,
  ICONS,
  Avatar,
  Pill,
  EmptyState,
  Spinner,
  inputClass,
  labelClass,
  btnPrimary,
  btnSecondary,
  Pagination,
  usePagination,
  DEFAULT_PAGE_SIZE,
} from "../../components/common/DashboardUI";
import ReceiptFileInput, { validateReceiptFile } from "../../components/common/ReceiptFileInput";

// Pull the top-level message and field-level `errors` map out of an axios
// error (backend Validator returns { message, errors: { field: [msg,...] } }
// on a 422).
function errInfo(err, fallback) {
  const data = err?.response?.data;
  return { message: data?.message || fallback, fields: data?.errors || {} };
}

// Mirrors MembersManagement.jsx's own local Modal — this codebase duplicates
// this small wrapper per admin page rather than sharing one.
function Modal({ title, onClose, children, size = "md" }) {
  const width = size === "lg" ? "max-w-2xl" : "max-w-md";
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className={`relative w-full ${width} bg-white rounded-2xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition"
        >
          <Icon path={ICONS.x} className="w-5 h-5" width={2} />
        </button>
        <h2 className="text-lg font-bold mb-4 pr-8" style={{ color: TOKENS.NAVY }}>
          {title}
        </h2>
        {children}
      </div>
    </div>
  );
}

// ── Deactivate club (reason optional, matches the backend's `nullable`
// validation) ────────────────────────────────────────────────────────────
function InactivateClubModal({ club, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    setError("");
    try {
      const r = await inactivateClub(club.id, reason.trim());
      toast.success(r?.message || "Club inactivated.");
      onDone();
    } catch (err) {
      setError(errInfo(err, "Could not inactivate this club.").message);
      setSaving(false);
    }
  };

  return (
    <Modal title="Deactivate Club" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <p className="text-sm text-gray-500">
          <span className="font-semibold">{club.club_name}</span> and every coach on it will be marked Inactive,
          and their portal sessions ended immediately. The club can be reactivated later.
        </p>
        <div>
          <label className={labelClass}>Reason (optional)</label>
          <textarea
            className={`${inputClass} resize-none`}
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            autoFocus
          />
        </div>
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex gap-3 pt-1">
          <button type="button" onClick={onClose} className={`${btnSecondary} flex-1`}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className={`${btnPrimary} flex-1`}>
            {saving ? "Deactivating…" : "Deactivate"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// Admin/staff edit form for an existing club — PATCH /api/clubs/{id} via
// updateClub(), gated by "edit_clubs". Every field is optional server-side,
// but the fields shown here are kept required in the UI (blanking one out
// and saving would clear it) except the two optional ones the backend itself
// treats as nullable-ish in practice (secondary phone).
function EditClubForm({ club, onCancel, onSaved }) {
  const [clubName, setClubName] = useState(club.club_name || "");
  const [regYear, setRegYear] = useState(club.reg_year || "");
  const [regNo, setRegNo] = useState(club.reg_no || "");
  const [membershipStart, setMembershipStart] = useState((club.membership_start_date || "").slice(0, 10));
  const [membershipEnd, setMembershipEnd] = useState((club.membership_end_date || "").slice(0, 10));
  const [primaryPhone, setPrimaryPhone] = useState(club.primary_phone_number || "");
  const [secondaryPhone, setSecondaryPhone] = useState(club.secondary_phone_number || "");
  const [address, setAddress] = useState(club.address || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const fieldError = (key) => {
    const msgs = fieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    setError("");
    setFieldErrors({});
    setSaving(true);
    try {
      const updated = await updateClub(club.id, {
        club_name: clubName,
        reg_year: regYear,
        reg_no: regNo,
        membership_start_date: membershipStart,
        membership_end_date: membershipEnd,
        primary_phone_number: primaryPhone,
        secondary_phone_number: secondaryPhone,
        address,
      });
      toast.success("Club updated.");
      onSaved(updated);
    } catch (err) {
      const info = errInfo(err, "Could not update the club.");
      setError(info.message);
      setFieldErrors(info.fields);
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label className={labelClass}>Club name</label>
        <input className={inputClass} value={clubName} onChange={(e) => setClubName(e.target.value)} required />
        {fieldError("club_name") && <p className="text-xs text-red-600 mt-1">{fieldError("club_name")}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Registration year</label>
          <input className={inputClass} value={regYear} onChange={(e) => setRegYear(e.target.value)} maxLength={4} required />
          {fieldError("reg_year") && <p className="text-xs text-red-600 mt-1">{fieldError("reg_year")}</p>}
        </div>
        <div>
          <label className={labelClass}>Registration no.</label>
          <input className={inputClass} value={regNo} onChange={(e) => setRegNo(e.target.value)} required />
          {fieldError("reg_no") && <p className="text-xs text-red-600 mt-1">{fieldError("reg_no")}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Membership start</label>
          <input
            type="date"
            className={inputClass}
            value={membershipStart}
            onChange={(e) => setMembershipStart(e.target.value)}
            required
          />
          {fieldError("membership_start_date") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("membership_start_date")}</p>
          )}
        </div>
        <div>
          <label className={labelClass}>Membership end</label>
          <input
            type="date"
            className={inputClass}
            value={membershipEnd}
            onChange={(e) => setMembershipEnd(e.target.value)}
            required
          />
          {fieldError("membership_end_date") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("membership_end_date")}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Primary phone</label>
          <input
            className={inputClass}
            value={primaryPhone}
            onChange={(e) => setPrimaryPhone(e.target.value)}
            required
          />
          {fieldError("primary_phone_number") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("primary_phone_number")}</p>
          )}
        </div>
        <div>
          <label className={labelClass}>Secondary phone</label>
          <input className={inputClass} value={secondaryPhone} onChange={(e) => setSecondaryPhone(e.target.value)} />
          {fieldError("secondary_phone_number") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("secondary_phone_number")}</p>
          )}
        </div>
      </div>

      <div>
        <label className={labelClass}>Address</label>
        <textarea
          className={`${inputClass} resize-none`}
          rows={2}
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          required
        />
        {fieldError("address") && <p className="text-xs text-red-600 mt-1">{fieldError("address")}</p>}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className={btnSecondary} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className={btnPrimary} disabled={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </form>
  );
}

// ── Pending Registrations tab (club registration processes) ────────────────
const money = (v) => (v == null || v === "" ? "—" : `LKR ${Number(v).toLocaleString("en-LK", { minimumFractionDigits: 2 })}`);

const fmtDateTime = (v) => {
  if (!v) return "—";
  const s = String(v).replace("T", " ");
  return s.length >= 16 ? s.slice(0, 16) : s;
};

// Read-only field grid used on the process detail page below.
function ProcessDetailGrid({ fields }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
      {fields
        .filter(([, v]) => v !== null && v !== undefined && v !== "")
        .map(([label, value]) => (
          <div key={label} className="min-w-0">
            <dt className="text-xs text-gray-400">{label}</dt>
            <dd className="text-sm font-medium text-gray-700 break-words" style={{ color: TOKENS.NAVY }}>
              {value}
            </dd>
          </div>
        ))}
    </dl>
  );
}

// Read-only detail page for a single club registration process — every
// attempt (pending, paid-but-unfinalized, completed), not just finalized
// clubs. Moved here from the old standalone "Registration Processes" admin
// page; now reachable only via the "Pending Registrations" sub-tab below.
function ClubProcessDetailPage() {
  const { processId } = useParams();
  const navigate = useNavigate();
  const [process, setProcess] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let on = true;
    setLoading(true);
    getClubRegistrationProcess(processId)
      .then((p) => on && setProcess(p))
      .catch((err) => on && setError(errInfo(err, "Could not load this registration process.").message))
      .finally(() => on && setLoading(false));
    return () => {
      on = false;
    };
  }, [processId]);

  return (
    <div>
      <button
        onClick={() => navigate("/admin/clubs")}
        className="inline-flex items-center gap-1.5 text-sm font-semibold mb-4 text-gray-500 hover:text-gray-800"
      >
        <Icon path={ICONS.chevron} className="w-4 h-4 rotate-90" width={2} />
        Back to Clubs
      </button>
      <div className="bg-white rounded-2xl shadow-sm p-6 max-w-3xl">
        <h1 className="text-lg font-bold mb-5" style={{ color: TOKENS.NAVY }}>
          Club Registration Process
        </h1>
        {loading ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <Avatar name={process.club_name} size={56} />
              <div className="min-w-0">
                <p className="text-base font-bold truncate" style={{ color: TOKENS.NAVY }}>
                  {process.club_name}
                </p>
                <p className="text-xs text-gray-400 truncate">{process.reg_no}</p>
              </div>
              <div className="ml-auto flex flex-col items-end gap-1">
                <Pill>{process.process_status}</Pill>
                <Pill>{process.payment_status}</Pill>
              </div>
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase text-gray-400 mb-3">Club</h3>
              <ProcessDetailGrid
                fields={[
                  ["Primary phone", process.primary_phone_number],
                  ["Secondary phone", process.secondary_phone_number],
                  ["Address", process.address],
                  ["No. of coaches", process.no_of_coaches],
                  ["Final club name", process.final_club_name],
                  ["Final club reg. no", process.final_club_reg_no],
                ]}
              />
            </div>

            <div>
              <h3 className="text-xs font-bold uppercase text-gray-400 mb-3">Payment</h3>
              <ProcessDetailGrid
                fields={[
                  ["Income", process.income_description],
                  ["Ledger code", process.ledger_code],
                  ["Payment reference", process.payment_reference],
                  ["External reference", process.external_payment_reference],
                  ["Payable amount", money(process.payable_amount)],
                  ["Paid amount", money(process.paid_amount)],
                  ["Paid at", fmtDateTime(process.paid_at)],
                ]}
              />
            </div>

            {Array.isArray(process.coaches) && process.coaches.length > 0 && (
              <div>
                <h3 className="text-xs font-bold uppercase text-gray-400 mb-3">Coaches ({process.coaches.length})</h3>
                <div className="space-y-2">
                  {process.coaches.map((c, i) => (
                    <div key={c.id ?? i} className="flex items-center justify-between gap-3 rounded-lg border border-gray-100 px-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                          {c.full_name || c.name || "—"}
                        </p>
                        <p className="text-xs text-gray-400 truncate">{c.email}</p>
                      </div>
                      <Pill>{c.coach_role || c.process_status}</Pill>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <h3 className="text-xs font-bold uppercase text-gray-400 mb-3">Timeline</h3>
              <ProcessDetailGrid fields={[["Created", fmtDateTime(process.created_at)], ["Updated", fmtDateTime(process.updated_at)]]} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Confirm Shroff Payment tab (manual registration-payment reconciliation) ─
// Look up a club registration process by its payment_reference and submit a
// shroff (walk-in) counter receipt for it, instead of going through the
// CyberExt gateway. Search is gated by "view_payments"; the submit form only
// renders for "submit_manual_payment_receipts" — see
// RegistrationPaymentController::manualClubSearch/manualClubConfirm.
// Submitting marks the payment Paid immediately. It also queues the receipt
// for a Director/Admin to acknowledge on the Payments page's "Manual
// Approvals" tab (see ManualPaymentApprovalController) — that step is
// audit-only and doesn't gate the payment.
function ConfirmShroffPaymentTab({ canConfirm }) {
  const [reference, setReference] = useState("");
  const [searching, setSearching] = useState(false);
  const [payment, setPayment] = useState(null);
  const [searchError, setSearchError] = useState("");

  const [paidAmount, setPaidAmount] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [receiptReference, setReceiptReference] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [confirmFieldErrors, setConfirmFieldErrors] = useState({});
  const [confirmResult, setConfirmResult] = useState(null);

  const fieldError = (key) => {
    const msgs = confirmFieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  const resetConfirmForm = (p) => {
    // Left blank on purpose — the operator types the amount actually received.
    setPaidAmount("");
    setPaidDate(new Date().toISOString().slice(0, 10));
    setReceiptReference("");
    setReceiptFile(null);
    setNote("");
    setConfirmError("");
    setConfirmFieldErrors({});
    setConfirmResult(null);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const ref = reference.trim();
    if (!ref || searching) return;
    setSearching(true);
    setSearchError("");
    setPayment(null);
    setConfirmResult(null);
    try {
      const found = await searchClubRegistrationPayment(ref);
      setPayment(found);
      resetConfirmForm(found);
    } catch (err) {
      setSearchError(errInfo(err, "No club registration payment found for that reference.").message);
    } finally {
      setSearching(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (confirming || !payment || !receiptFile) return;
    setConfirmError("");
    setConfirmFieldErrors({});
    setConfirming(true);
    try {
      const result = await confirmClubRegistrationPayment({
        payment_reference: payment.payment_reference,
        paid_amount: paidAmount,
        paid_date: paidDate,
        receipt_reference: receiptReference,
        receipt: receiptFile,
        note: note || undefined,
      });
      toast.success(result.message || "Payment confirmed. Receipt pending director acknowledgement.");
      setConfirmResult(result);
    } catch (err) {
      const info = errInfo(err, "Could not submit this receipt.");
      setConfirmError(info.message);
      setConfirmFieldErrors(info.fields);
    } finally {
      setConfirming(false);
    }
  };

  const isPending = payment?.payment_status === "Pending";

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <h3 className="font-bold text-base mb-1" style={{ color: TOKENS.NAVY }}>
          Find a registration payment
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          Look up a club's registration payment by its reference number, then record a payment taken at the shroff
          counter.
        </p>
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
          <input
            className={`${inputClass} flex-1 min-w-[220px] font-mono`}
            placeholder="Payment reference, e.g. 07302000001"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
          />
          <button type="submit" disabled={searching || !reference.trim()} className={`${btnPrimary} !w-auto px-6`}>
            {searching ? "Searching…" : "Search"}
          </button>
        </form>
        {searchError && <p className="text-sm text-red-600 mt-3">{searchError}</p>}
      </div>

      {payment && (
        <div className="bg-white rounded-2xl shadow-sm p-6 space-y-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-base font-bold truncate" style={{ color: TOKENS.NAVY }}>
                {payment.club?.club_name}
              </p>
              <p className="text-xs text-gray-400 truncate">{payment.club?.reg_no}</p>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <Pill>{payment.payment_status}</Pill>
              <Pill>{payment.process_status}</Pill>
            </div>
          </div>

          <ProcessDetailGrid
            fields={[
              ["Payment reference", payment.payment_reference],
              ["Payable amount", money(payment.payable_amount)],
              ["Paid amount", money(payment.paid_amount)],
              ["Paid at", fmtDateTime(payment.paid_at)],
              ["Phone", payment.club?.primary_phone_number],
              ["No. of coaches", payment.club?.no_of_coaches],
            ]}
          />

          {!isPending ? (
            <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">
              This payment is already <strong>{payment.payment_status}</strong> — nothing to confirm.
            </p>
          ) : !canConfirm ? (
            <p className="text-sm text-gray-500 bg-gray-50 rounded-lg px-4 py-3">
              You don't have permission to confirm this payment.
            </p>
          ) : (
            <form onSubmit={handleConfirm} className="space-y-4 pt-4 border-t border-gray-100">
              <h4 className="text-sm font-bold" style={{ color: TOKENS.NAVY }}>
                Submit Shroff Receipt
              </h4>
              <p className="text-xs text-gray-400 -mt-2">
                Submitting confirms this payment immediately. A Director/Admin will acknowledge the receipt
                afterward for audit purposes.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>
                    Paid amount <span className="text-gray-400 font-normal">(must match payable amount)</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={inputClass}
                    value={paidAmount}
                    onChange={(e) => setPaidAmount(e.target.value)}
                    required
                  />
                  {fieldError("paid_amount") && <p className="text-xs text-red-600 mt-1">{fieldError("paid_amount")}</p>}
                </div>
                <div>
                  <label className={labelClass}>Paid date</label>
                  <input
                    type="date"
                    className={inputClass}
                    value={paidDate}
                    onChange={(e) => setPaidDate(e.target.value)}
                    required
                  />
                  {fieldError("paid_date") && <p className="text-xs text-red-600 mt-1">{fieldError("paid_date")}</p>}
                </div>
              </div>
              <div>
                <label className={labelClass}>Receipt / slip reference</label>
                <input
                  className={inputClass}
                  value={receiptReference}
                  onChange={(e) => setReceiptReference(e.target.value)}
                  required
                />
                {fieldError("receipt_reference") && (
                  <p className="text-xs text-red-600 mt-1">{fieldError("receipt_reference")}</p>
                )}
              </div>
              <ReceiptFileInput file={receiptFile} onChange={setReceiptFile} error={fieldError("receipt")} />
              <div>
                <label className={labelClass}>
                  Note <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  className={`${inputClass} resize-none`}
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                {fieldError("note") && <p className="text-xs text-red-600 mt-1">{fieldError("note")}</p>}
              </div>

              {confirmError && <p className="text-sm text-red-600">{confirmError}</p>}

              <button type="submit" disabled={confirming || !receiptFile} className={`${btnPrimary} w-full`}>
                {confirming ? "Confirming…" : "Confirm Payment"}
              </button>
            </form>
          )}

          {confirmResult && (
            <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-4 py-3 text-sm text-emerald-800">
              {confirmResult.message || "Payment confirmed. Receipt pending director acknowledgement."}
              {confirmResult.approval_id && (
                <>
                  {" "}
                  Approval ID: <span className="font-mono font-semibold">#{confirmResult.approval_id}</span> — will
                  be acknowledged on the Payments page's Manual Approvals tab.
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── My Submissions tab (this instructor's own shroff receipt submissions) ──
// Read-only: the my-submissions endpoint isn't scoped to members vs clubs —
// it returns every receipt this system user has submitted regardless of
// payable type — so a club payment submitted from this page and a member
// payment submitted from the Members page both show up here. Gated by
// "submit_manual_payment_receipts" (same permission needed to submit one) —
// see ManualPaymentApprovalController::mySubmissions.
function MySubmissionsTab() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [missingReceiptOnly, setMissingReceiptOnly] = useState(false);
  const [viewingReceiptId, setViewingReceiptId] = useState(null);
  const [uploadingReceiptId, setUploadingReceiptId] = useState(null);

  // Streams the scan through the authenticated receipt route — the
  // submitter's own submit_manual_payment_receipts permission is enough.
  const handleViewReceipt = async (approvalId) => {
    if (viewingReceiptId) return;
    const tab = window.open("", "_blank"); // synchronous, so the popup blocker allows it
    setViewingReceiptId(approvalId);
    try {
      const blob = await getManualPaymentReceiptFile(approvalId);
      const url = URL.createObjectURL(blob);
      if (tab && !tab.closed) tab.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      tab?.close?.();
      toast.error(errInfo(err, "Could not open this receipt.").message);
    } finally {
      setViewingReceiptId(null);
    }
  };

  // Backfills a receipt onto a submission made before receipts were required.
  const handleUploadReceipt = async (approvalId, file) => {
    const invalid = validateReceiptFile(file);
    if (invalid) {
      toast.error(invalid);
      return;
    }
    setUploadingReceiptId(approvalId);
    try {
      const result = await uploadManualPaymentReceipt(approvalId, file);
      toast.success(result.message || "Payment receipt uploaded.");
      fetchSubmissions();
    } catch (err) {
      const info = errInfo(err, "Could not upload this receipt.");
      toast.error(info.fields?.receipt?.[0] || info.message);
    } finally {
      setUploadingReceiptId(null);
    }
  };

  const fetchSubmissions = useCallback(() => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (missingReceiptOnly) params.missing_receipt = 1;
    listMyManualPaymentSubmissions(params)
      .then(setSubmissions)
      .catch((err) => toast.error(errInfo(err, "Could not load your submissions.").message))
      .finally(() => setLoading(false));
  }, [statusFilter, missingReceiptOnly]);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const { pageItems, page, totalPages, setPage } = usePagination(submissions, {
    pageSize: DEFAULT_PAGE_SIZE,
    resetKey: `${statusFilter}|${missingReceiptOnly}`,
  });

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select className={`${inputClass} w-auto`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">All statuses</option>
          <option value="Pending">Pending</option>
          <option value="Approved">Acknowledged</option>
          <option value="Rejected">Rejected</option>
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={missingReceiptOnly}
            onChange={(e) => setMissingReceiptOnly(e.target.checked)}
          />
          Missing receipt only
        </label>
      </div>
      <div className="bg-white rounded-2xl shadow-sm p-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-14 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        ) : submissions.length === 0 ? (
          <EmptyState icon={ICONS.payments} text="You haven't submitted any shroff receipts yet." />
        ) : (
          <div className="overflow-x-auto -mx-6 px-6">
            <table className="w-full text-left min-w-[880px]">
              <thead>
                <tr className="text-xs text-gray-400 border-b border-gray-100">
                  <th className="py-2 pr-3 font-medium">Receipt Ref</th>
                  <th className="py-2 px-3 font-medium">For</th>
                  <th className="py-2 px-3 font-medium">Amount</th>
                  <th className="py-2 px-3 font-medium">Submitted</th>
                  <th className="py-2 px-3 font-medium">Status</th>
                  <th className="py-2 px-3 font-medium">Receipt</th>
                  <th className="py-2 pl-3 font-medium">Rejection Note</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((s) => (
                  <tr key={s.approval_id} className="border-b border-gray-50 last:border-0">
                    <td className="py-3 pr-3 text-sm font-mono text-gray-600 whitespace-nowrap">{s.receipt_reference}</td>
                    <td className="py-3 px-3 text-sm font-semibold whitespace-nowrap" style={{ color: TOKENS.NAVY }}>
                      {s.payable?.member_name || s.payable?.club_name || "—"}
                    </td>
                    <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{money(s.amount_paid)}</td>
                    <td className="py-3 px-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(s.submitted_at)}</td>
                    <td className="py-3 px-3">
                      <Pill>{s.approval_status === "Approved" ? "Acknowledged" : s.approval_status}</Pill>
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      {s.has_receipt_file ? (
                        <button
                          type="button"
                          onClick={() => handleViewReceipt(s.approval_id)}
                          disabled={viewingReceiptId === s.approval_id}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-blue-700 hover:text-blue-900 disabled:opacity-50"
                        >
                          <Icon path={ICONS.download} className="w-4 h-4" width={1.8} />
                          {viewingReceiptId === s.approval_id ? "Opening…" : "View"}
                        </button>
                      ) : (
                        <label
                          className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 ${
                            uploadingReceiptId ? "opacity-50 cursor-wait" : "cursor-pointer hover:bg-amber-100"
                          }`}
                          title="No receipt attached — click to upload one (PDF or image, max 5MB)"
                        >
                          <Icon path={ICONS.plus} className="w-3.5 h-3.5" width={2} />
                          {uploadingReceiptId === s.approval_id ? "Uploading…" : "Upload receipt"}
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png,.webp"
                            className="hidden"
                            disabled={uploadingReceiptId !== null}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              e.target.value = "";
                              if (file) handleUploadReceipt(s.approval_id, file);
                            }}
                          />
                        </label>
                      )}
                    </td>
                    <td className="py-3 pl-3 text-sm text-gray-600 max-w-[240px] truncate" title={s.rejection_note || ""}>
                      {s.approval_status === "Rejected" ? s.rejection_note || "—" : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} className="mt-4" />
      </div>
    </div>
  );
}

// Payment-type badge — mirrors the `payment_for` discriminator GET
// /api/clubs/{id}/payments merges across (PaymentController::forClub):
// member/club registration, membership/club renewal, ID card reprint,
// facility booking. Duplicated from MembersManagement.jsx's own copy rather
// than shared, matching this codebase's per-page UI convention.
const PAYMENT_TYPE_BADGE = {
  member_registration: { bg: "#ede9fe", color: "#6d28d9", label: "Registration" },
  club_registration: { bg: "#ede9fe", color: "#6d28d9", label: "Club Registration" },
  renewal: { bg: "#e0f2fe", color: "#0369a1", label: "Renewal" },
  club_renewal: { bg: "#e0f2fe", color: "#0369a1", label: "Club Renewal" },
  reprint: { bg: "#fce7f3", color: "#be185d", label: "ID Card Reprint" },
  booking: { bg: "#dbeafe", color: "#1d4ed8", label: "Booking" },
};

function PaymentTypeBadge({ type }) {
  const t = PAYMENT_TYPE_BADGE[type] || { bg: "#e5e7eb", color: "#4b5563", label: type || "—" };
  return (
    <span
      className="text-xs font-semibold px-2.5 py-0.5 rounded-full whitespace-nowrap"
      style={{ backgroundColor: t.bg, color: t.color }}
    >
      {t.label}
    </span>
  );
}

// The /api/clubs/{id}/payments merge doesn't return one unified
// "description" field — each payment type's row only carries what that
// resource actually has, so this assembles a readable line from whatever
// that row has rather than inventing detail.
function paymentDescription(p) {
  const base = p.income_description || PAYMENT_TYPE_BADGE[p.payment_for]?.label || "Payment";
  if ((p.payment_for === "renewal" || p.payment_for === "club_renewal") && p.new_membership_start_date && p.new_membership_end_date) {
    return `${base} (${String(p.new_membership_start_date).slice(0, 10)} to ${String(p.new_membership_end_date).slice(0, 10)})`;
  }
  if (p.payment_for === "member_registration" && p.payer_name) return `${base} — ${p.payer_name}`;
  return base;
}

function SummaryStat({ icon, bg, color, label, value, sub }) {
  return (
    <div className="rounded-xl border border-gray-100 p-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-2" style={{ backgroundColor: bg }}>
        <Icon path={icon} className="w-4 h-4" stroke={color} width={2} />
      </div>
      <p className="text-lg font-bold truncate" style={{ color: TOKENS.NAVY }}>
        {value}
      </p>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-[10px] text-gray-400">{sub}</p>
    </div>
  );
}

// ── Club detail (read-only, own page) ───────────────────────────────────────
function ClubDetailPage() {
  const { clubId } = useParams();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(true);
  const [paymentsError, setPaymentsError] = useState("");
  const [paymentSearch, setPaymentSearch] = useState("");

  useEffect(() => {
    let on = true;
    setLoading(true);
    getClub(clubId)
      .then((c) => on && setClub(c))
      .catch((err) => on && setError(err?.response?.data?.message || "Could not load club details."))
      .finally(() => on && setLoading(false));
    return () => {
      on = false;
    };
  }, [clubId]);

  // Every payment tied to this club — registration, individual coach
  // registrations, club/member renewals, ID card reprints and facility
  // bookings, all merged by GET /api/clubs/{id}/payments
  // (PaymentController::forClub) — see getClubPayments() in
  // paymentService.js. Fetched off the route's own clubId directly (the
  // numeric id forClub needs), so this doesn't have to wait on `club` to
  // load first the way the Member page's payments effect waits on
  // member.member_code.
  useEffect(() => {
    if (!clubId) return undefined;
    let on = true;
    setPaymentsLoading(true);
    setPaymentsError("");
    getClubPayments(clubId)
      .then((rows) => on && setPayments(rows))
      .catch((err) => on && setPaymentsError(errInfo(err, "Could not load payment history.").message))
      .finally(() => on && setPaymentsLoading(false));
    return () => {
      on = false;
    };
  }, [clubId]);

  const totalPayments = payments.length;
  const totalAmount = payments.reduce((sum, p) => sum + (Number(p.paid_amount ?? p.payable_amount) || 0), 0);
  const paidCount = payments.filter((p) => String(p.payment_status || "").toLowerCase().includes("paid")).length;
  const pendingCount = payments.filter((p) => String(p.payment_status || "").toLowerCase().includes("pending")).length;

  const filteredPayments = paymentSearch.trim()
    ? payments.filter((p) => {
        const q = paymentSearch.trim().toLowerCase();
        return (
          String(p.payment_reference || "").toLowerCase().includes(q) ||
          String(PAYMENT_TYPE_BADGE[p.payment_for]?.label || p.payment_for || "").toLowerCase().includes(q) ||
          paymentDescription(p).toLowerCase().includes(q) ||
          String(p.payment_status || "").toLowerCase().includes(q)
        );
      })
    : payments;

  const clubPaymentsPagination = usePagination(filteredPayments, { pageSize: DEFAULT_PAGE_SIZE, resetKey: paymentSearch });

  const fields = club
    ? [
        ["Club code", club.club_code],
        ["Registration no.", club.reg_no],
        ["Registration year", club.reg_year],
        ["Status", club.club_status],
        ["Primary phone", club.primary_phone_number],
        ["Secondary phone", club.secondary_phone_number],
        ["Address", club.address],
        ["Coaches", club.no_of_coaches],
        ["Membership start", club.membership_start_date],
        ["Membership end", club.membership_end_date],
        ["Approved members", club.approved_member_count],
        ["Pending member requests", club.pending_member_request_count],
      ].filter(([, v]) => v != null && v !== "")
    : [];

  return (
    <div>
      <button
        onClick={() => navigate("/admin/clubs")}
        className="inline-flex items-center gap-1.5 text-sm font-semibold mb-4 text-gray-500 hover:text-gray-800"
      >
        <Icon path={ICONS.chevron} className="w-4 h-4 rotate-90" width={2} />
        Back to Clubs
      </button>
      {loading ? (
        <div className="bg-white rounded-2xl shadow-sm p-6 max-w-2xl">
          <div className="flex flex-col items-center justify-center py-14 gap-3">
            <Spinner />
            <p className="text-sm text-gray-400">Loading…</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-white rounded-2xl shadow-sm p-6 max-w-2xl">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
            {/* Club overview */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm p-6">
              <h1 className="text-lg font-bold mb-5" style={{ color: TOKENS.NAVY }}>
                Club Details
              </h1>
              <div className="space-y-5">
                <h3 className="text-base font-bold" style={{ color: TOKENS.NAVY }}>
                  {club.club_name}
                </h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
                  {fields.map(([label, value]) => (
                    <div key={label} className="min-w-0">
                      <dt className="text-xs text-gray-400">{label}</dt>
                      <dd className="text-base font-medium text-gray-700 truncate" style={{ color: TOKENS.NAVY }}>
                        {value}
                      </dd>
                    </div>
                  ))}
                </dl>

                {club.recent_members?.length > 0 && (
                  <div>
                    <h4 className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2">Recent Members</h4>
                    <div className="space-y-1.5">
                      {club.recent_members.map((m) => (
                        <div key={m.id} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-50 last:border-0">
                          <span className="text-gray-700">
                            {[m.title, m.name_denoted_by_initials, m.lastname].filter(Boolean).join(" ")}
                          </span>
                          <span className="text-xs text-gray-400 font-mono">{m.member_code}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Payment summary */}
            <div className="lg:col-span-1 bg-white rounded-2xl shadow-sm p-6">
              <h3 className="text-base font-bold mb-4" style={{ color: TOKENS.NAVY }}>
                Payment Summary
              </h3>
              <div className="grid grid-cols-2 gap-3">
                <SummaryStat
                  icon={ICONS.bookings}
                  bg="#dbeafe"
                  color="#1d4ed8"
                  label="Total Payments"
                  value={paymentsLoading ? "…" : totalPayments}
                  sub="All Time"
                />
                <SummaryStat
                  icon={ICONS.payments}
                  bg="#dcfce7"
                  color="#15803d"
                  label="Total Amount"
                  value={paymentsLoading ? "…" : money(totalAmount)}
                  sub="All Time"
                />
                <SummaryStat
                  icon={ICONS.check}
                  bg="#dcfce7"
                  color="#15803d"
                  label="Paid"
                  value={paymentsLoading ? "…" : paidCount}
                  sub="Payments"
                />
                <SummaryStat
                  icon={ICONS.clock}
                  bg="#ffedd5"
                  color="#c2410c"
                  label="Pending"
                  value={paymentsLoading ? "…" : pendingCount}
                  sub="Payment(s)"
                />
              </div>
            </div>
          </div>

          {/* All payments */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h3 className="text-base font-bold" style={{ color: TOKENS.NAVY }}>
                  All Payments
                </h3>
                <p className="text-xs text-gray-400">
                  All transactions for this club (Registrations, Renewals, Bookings &amp; more).
                </p>
              </div>
              <div className="relative w-full sm:w-64">
                <input
                  className={`${inputClass} pr-9`}
                  placeholder="Search payments…"
                  value={paymentSearch}
                  onChange={(e) => setPaymentSearch(e.target.value)}
                />
                <Icon
                  path={ICONS.search}
                  className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                  stroke="#9ca3af"
                  width={2}
                />
              </div>
            </div>

            {paymentsLoading ? (
              <div className="flex items-center justify-center gap-2 py-10">
                <Spinner />
                <p className="text-sm text-gray-400">Loading payments…</p>
              </div>
            ) : paymentsError ? (
              <p className="text-sm text-red-600 py-4">{paymentsError}</p>
            ) : filteredPayments.length === 0 ? (
              <EmptyState
                icon={ICONS.payments}
                text={payments.length === 0 ? "No payment records found for this club." : "No payments match your search."}
              />
            ) : (
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                      <th className="py-2 px-3 font-semibold">#</th>
                      <th className="py-2 px-3 font-semibold">Payment ID</th>
                      <th className="py-2 px-3 font-semibold">Type</th>
                      <th className="py-2 px-3 font-semibold">Description</th>
                      <th className="py-2 px-3 font-semibold">Amount</th>
                      <th className="py-2 px-3 font-semibold">Status</th>
                      <th className="py-2 px-3 font-semibold">Paid At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clubPaymentsPagination.pageItems.map((p, i) => (
                      <tr
                        key={`${p.payment_for}_${p.id ?? p.process_id ?? p.renewal_id ?? p.reprint_id ?? p.payment_id ?? i}`}
                        className="border-b border-gray-50 last:border-0"
                      >
                        <td className="py-3 px-3 text-gray-400">
                          {(clubPaymentsPagination.page - 1) * DEFAULT_PAGE_SIZE + i + 1}
                        </td>
                        <td className="py-3 px-3 font-semibold whitespace-nowrap" style={{ color: TOKENS.NAVY }}>
                          {p.payment_reference || "—"}
                        </td>
                        <td className="py-3 px-3">
                          <PaymentTypeBadge type={p.payment_for} />
                        </td>
                        <td className="py-3 px-3 text-gray-600">{paymentDescription(p)}</td>
                        <td className="py-3 px-3 font-semibold whitespace-nowrap" style={{ color: TOKENS.NAVY }}>
                          {money(p.paid_amount ?? p.payable_amount)}
                        </td>
                        <td className="py-3 px-3">
                          <Pill>{p.payment_status}</Pill>
                        </td>
                        <td className="py-3 px-3 text-gray-500 whitespace-nowrap">{p.paid_at ? fmtDateTime(p.paid_at) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {!paymentsLoading && !paymentsError && payments.length > 0 && (
              <p className="text-xs text-gray-400 mt-4">
                Showing {filteredPayments.length} of {payments.length} payment{payments.length === 1 ? "" : "s"}.
              </p>
            )}
            <Pagination
              page={clubPaymentsPagination.page}
              totalPages={clubPaymentsPagination.totalPages}
              onPageChange={clubPaymentsPagination.setPage}
              className="mt-4"
            />
          </div>
        </div>
      )}
    </div>
  );
}

const emptyCoach = () => ({
  id: `coach-${Math.random().toString(36).slice(2)}`,
  title: "",
  initials: "",
  nameWithInitials: "",
  lastName: "",
  memberGenderId: "",
  email: "",
  nic: "",
  primaryPhone: "",
  secondaryPhone: "",
  dob: "",
  address: "",
  photo: null,
});

// One coach's fields inside the "Add Club" form — shared by the (fixed)
// Primary Coach and each removable "Other Coach". A photo is required only
// for the Primary Coach (photoRequired prop), matching the public
// /club-registration flow's rule; other coaches' photos are optional.
function CoachFields({ label, coach, genders, titles, error, onChange, onRemove, photoRequired }) {
  const fieldError = (key) => error?.[key] || null;
  // Same look as `inputClass` (see DashboardUI) but with the border/ring
  // colour split out, so a coach field with a backend error can turn red
  // without fighting `inputClass`'s own gray/blue classes for precedence.
  const fieldClass = (key) =>
    `w-full border rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 transition ${
      fieldError(key)
        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
        : "border-gray-200 focus:ring-blue-500 focus:border-blue-500"
    }`;
  return (
    <div className="rounded-xl border border-gray-200 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-bold" style={{ color: TOKENS.NAVY }}>
          {label}
        </p>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs font-semibold text-red-500 hover:text-red-600"
          >
            Remove
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className={labelClass}>Title</label>
          <select
            className={fieldClass("title")}
            value={coach.title}
            onChange={(e) => onChange("title", e.target.value)}
            required
          >
            <option value="" disabled>
              Select…
            </option>
            {titles.map((t) => (
              <option key={t.category_id} value={t.description}>
                {t.description}
              </option>
            ))}
          </select>
          {fieldError("title") && <p className="text-xs text-red-600 mt-1">{fieldError("title")}</p>}
        </div>
        <div>
          <label className={labelClass}>Gender</label>
          <select
            className={fieldClass("memberGenderId")}
            value={coach.memberGenderId}
            onChange={(e) => onChange("memberGenderId", e.target.value)}
            required
          >
            <option value="" disabled>
              Select…
            </option>
            {genders.map((g) => (
              <option key={g.category_id} value={g.category_id}>
                {g.description}
              </option>
            ))}
          </select>
          {fieldError("memberGenderId") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("memberGenderId")}</p>
          )}
        </div>
        <div>
          <label className={labelClass}>
            Initials <span className="text-gray-400 font-normal">(e.g. A.B.)</span>
          </label>
          <input
            className={fieldClass("initials")}
            value={coach.initials}
            onChange={(e) => onChange("initials", e.target.value)}
            required
          />
          {fieldError("initials") && <p className="text-xs text-red-600 mt-1">{fieldError("initials")}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className={labelClass}>Name denoted by initials</label>
          <input
            className={fieldClass("nameWithInitials")}
            value={coach.nameWithInitials}
            onChange={(e) => onChange("nameWithInitials", e.target.value)}
            required
          />
          {fieldError("nameWithInitials") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("nameWithInitials")}</p>
          )}
        </div>

        <div>
          <label className={labelClass}>Last name</label>
          <input
            className={fieldClass("lastName")}
            value={coach.lastName}
            onChange={(e) => onChange("lastName", e.target.value)}
            required
          />
          {fieldError("lastName") && <p className="text-xs text-red-600 mt-1">{fieldError("lastName")}</p>}
        </div>

        <div>
          <label className={labelClass}>Email</label>
          <input
            type="email"
            className={fieldClass("email")}
            value={coach.email}
            onChange={(e) => onChange("email", e.target.value)}
            required
          />
          {fieldError("email") && <p className="text-xs text-red-600 mt-1">{fieldError("email")}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className={labelClass}>NIC</label>
          <input
            className={fieldClass("nic")}
            value={coach.nic}
            onChange={(e) => onChange("nic", e.target.value)}
            required
          />
          {fieldError("nic") && <p className="text-xs text-red-600 mt-1">{fieldError("nic")}</p>}
        </div>

        <div>
          <label className={labelClass}>Date of birth</label>
          <input
            type="date"
            className={fieldClass("dob")}
            value={coach.dob}
            max={new Date().toLocaleDateString("en-CA")}
            onChange={(e) => onChange("dob", e.target.value)}
          />
          {fieldError("dob") && <p className="text-xs text-red-600 mt-1">{fieldError("dob")}</p>}
        </div>

        <div>
          <label className={labelClass}>Primary phone</label>
          <input
            className={fieldClass("primaryPhone")}
            value={coach.primaryPhone}
            onChange={(e) => onChange("primaryPhone", e.target.value)}
            required
          />
          {fieldError("primaryPhone") && <p className="text-xs text-red-600 mt-1">{fieldError("primaryPhone")}</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <label className={labelClass}>Secondary phone</label>
          <input
            className={fieldClass("secondaryPhone")}
            value={coach.secondaryPhone}
            onChange={(e) => onChange("secondaryPhone", e.target.value)}
          />
          {fieldError("secondaryPhone") && (
            <p className="text-xs text-red-600 mt-1">{fieldError("secondaryPhone")}</p>
          )}
        </div>
      </div>

      <div>
        <label className={labelClass}>Address</label>
        <textarea
          className={`${fieldClass("address")} resize-none`}
          rows={2}
          value={coach.address}
          onChange={(e) => onChange("address", e.target.value)}
          required
        />
        {fieldError("address") && <p className="text-xs text-red-600 mt-1">{fieldError("address")}</p>}
      </div>

      <div>
        <label className={labelClass}>
          Photo{" "}
          <span className="text-gray-400 font-normal">
            {photoRequired ? "(required — used on the coach's QR card)" : "(optional — used on the coach's QR card)"}
          </span>
        </label>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className={fieldClass("photo")}
          onChange={(e) => onChange("photo", e.target.files?.[0] || null)}
          required={photoRequired}
        />
        {fieldError("photo") && <p className="text-xs text-red-600 mt-1">{fieldError("photo")}</p>}
      </div>
    </div>
  );
}

// Backend fields belonging to the "club" step of the Add Club wizard — used
// (together with the coaches.*/coach_photos.* dot-paths already split out
// separately) to decide, after a submit-time 422, whether to jump back to
// "club" rather than leaving the user on "payment" (the step the form is
// actually submitted from) for an error they can't see there.
const CLUB_STEP_FIELDS = new Set([
  "club_name",
  "reg_no",
  "primary_phone_number",
  "secondary_phone_number",
  "address",
  "membership_start_date",
  "membership_end_date",
]);

// ── Client-side field validators ────────────────────────────────────────
// Mirror the backend's own rules (see the public registration flow's
// equivalents in ClubRegistration.jsx / StudentRegistration.jsx) so a field
// never bounces between "client says ok" and "backend says invalid".
const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_PHOTO_SIZE_BYTES = 2 * 1024 * 1024; // 2MB

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
// Mobile-specific — used for a person's own primary phone: optional
// 0/94/+94 prefix + 7 + 8 digits.
const isValidMobilePhone = (value) => /^(?:\+94|94|0)?7\d{8}$/.test(value.replace(/\s/g, ""));
// Any local number (mobile or landline) — used for club phones and
// secondary phones, where a landline is plausible.
const isValidPhone = (value) => /^(?:\+94|94|0)?\d{9}$/.test(value.replace(/\s/g, ""));
// Single letters each followed by a dot, e.g. "T.N." or "A.B.C.".
const isValidInitials = (value) => /^([A-Za-z]\.\s?)+$/.test(value.trim());
// Old format: 9 digits + V/X. New format: 12 digits.
const isValidNIC = (value) => /^(?:\d{9}[vVxX]|\d{12})$/.test(value.trim());
const isNotFutureDate = (value) => !value || value <= new Date().toISOString().slice(0, 10);
const photoFileError = (file) => {
  if (!file) return null;
  if (!ALLOWED_PHOTO_TYPES.includes(file.type)) return "Photo must be a JPG, PNG, or WebP image.";
  if (file.size > MAX_PHOTO_SIZE_BYTES) return "Photo must be less than 2MB.";
  return null;
};

// ── Add Club form ────────────────────────────────────────────────────────
// Registers a brand-new club together with its coaches in one call
// (POST /api/clubs/legacy, no OTP — unlike the public /club-registration
// flow). Formerly reached only via the Members page's "Add Member" form
// (picking a synthetic "Club" member type); now lives directly under the
// Clubs page as its own "Add Club" tab.
function AddClubForm({ genders, titles, incomes, onCancel, onSaved }) {
  const [clubName, setClubName] = useState("");
  const [regNo, setRegNo] = useState("");
  const [regYear, setRegYear] = useState("");
  const [primaryPhone, setPrimaryPhone] = useState("");
  const [secondaryPhone, setSecondaryPhone] = useState("");
  const [address, setAddress] = useState("");
  const [membershipStart, setMembershipStart] = useState("");
  const [coaches, setCoaches] = useState(() => [emptyCoach()]);
  const [coachErrors, setCoachErrors] = useState({});

  // ── Payment-step fields ──────────────────────────────────────────────────
  // NOTE: the backend's /api/clubs/legacy endpoint does not yet validate or
  // persist these (unlike /api/members/legacy, which saves them to
  // legacy_member_payment_slips) — see ClubController::storeLegacy. They're
  // still sent below so nothing needs to change here once backend support
  // is added; until then they're silently ignored server-side.
  const [incomeId, setIncomeId] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [slipReference, setSlipReference] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [note, setNote] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const formRef = React.useRef(null);
  // Registration is a three-step wizard: "club" (club details) → "coaches"
  // (Primary Coach + any additional coaches) → "payment" (counter-payment
  // record), submitted together in one /api/clubs/legacy request once all
  // three steps are complete.
  const [step, setStep] = useState("club");

  const fieldError = (key) => {
    const msgs = fieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };

  // Same look as `inputClass` (see DashboardUI) but with the border/ring
  // colour split out, so a field with a backend error can turn red without
  // fighting `inputClass`'s own gray/blue classes for precedence.
  const fieldClass = (key) =>
    `w-full border rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 transition ${
      fieldError(key)
        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
        : "border-gray-200 focus:ring-blue-500 focus:border-blue-500"
    }`;

  // Clears one club-level field's error the moment the user edits it, so a
  // message doesn't sit there stale after the value's already been fixed.
  const clearFieldError = (key) =>
    setFieldErrors((fe) => (fe[key] ? { ...fe, [key]: null } : fe));

  const handleCoachChange = (id, field, value) => {
    setCoaches((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
    // Clear just this coach's field error, same as clearFieldError above but
    // scoped into the per-coach coachErrors map.
    setCoachErrors((prev) => (prev[id]?.[field] ? { ...prev, [id]: { ...prev[id], [field]: null } } : prev));
  };
  const addCoach = () => setCoaches((prev) => [...prev, emptyCoach()]);
  const removeCoach = (id) => {
    setCoaches((prev) => (prev.length === 1 ? prev : prev.filter((c) => c.id !== id)));
    setCoachErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // Client-side validation for the "club" step.
  const validateClubStep = () => {
    const errs = {};
    if (!clubName.trim()) errs.club_name = ["Please enter the club name."];
    if (!regNo.trim()) errs.reg_no = ["Please enter the registration number."];
    if (!String(regYear).trim()) errs.reg_year = ["Please enter the registration year."];
    else if (!/^\d{4}$/.test(String(regYear).trim()) || Number(regYear) > new Date().getFullYear())
      errs.reg_year = ["Enter a valid 4-digit year, not in the future."];
    if (!primaryPhone.trim()) errs.primary_phone_number = ["Please enter the primary phone."];
    else if (!isValidPhone(primaryPhone))
      errs.primary_phone_number = ["Enter a valid phone number (e.g. 0112345678 or +94112345678)."];
    if (secondaryPhone && !isValidPhone(secondaryPhone))
      errs.secondary_phone_number = ["Enter a valid phone number or leave it empty."];
    if (!address.trim()) errs.address = ["Please enter the address."];
    if (!membershipStart) errs.membership_start_date = ["Please select a membership start date."];
    return errs;
  };

  // Client-side validation for the "coaches" step — every coach (Primary +
  // any Other Coaches), keyed by coach.id to match `coachErrors`' shape.
  const validateCoachesStep = () => {
    const errs = {};
    coaches.forEach((c, i) => {
      const e = {};
      if (!c.title) e.title = "Please select a title.";
      if (!c.memberGenderId) e.memberGenderId = "Please select a gender.";
      if (!c.initials.trim()) e.initials = "Please enter the initials.";
      else if (!isValidInitials(c.initials)) e.initials = "Use a dot after each initial, e.g. A.B.";
      if (!c.nameWithInitials.trim()) e.nameWithInitials = "Please enter the name with initials.";
      if (!c.lastName.trim()) e.lastName = "Please enter the last name.";
      if (!c.email.trim()) e.email = "Please enter an email address.";
      else if (!isValidEmail(c.email)) e.email = "Enter a valid email address.";
      if (!c.nic.trim()) e.nic = "Please enter the NIC.";
      else if (!isValidNIC(c.nic)) e.nic = "Enter a valid NIC — 9 digits + V/X (e.g. 200012345V) or 12 digits.";
      if (!c.primaryPhone.trim()) e.primaryPhone = "Please enter the primary phone.";
      else if (!isValidMobilePhone(c.primaryPhone))
        e.primaryPhone = "Enter a valid mobile number (e.g. 0712345678 or +94712345678).";
      if (c.secondaryPhone && !isValidPhone(c.secondaryPhone))
        e.secondaryPhone = "Enter a valid phone number or leave it empty.";
      if (c.dob && !isNotFutureDate(c.dob)) e.dob = "Date of birth cannot be in the future.";
      if (!c.address.trim()) e.address = "Please enter the address.";
      const photoErr = photoFileError(c.photo);
      if (photoErr) e.photo = photoErr;
      else if (i === 0 && !c.photo) e.photo = "A profile photo is required for the primary coach.";
      if (Object.keys(e).length) errs[c.id] = e;
    });
    return errs;
  };

  // Client-side validation for the "payment" step.
  const validatePaymentStep = () => {
    const errs = {};
    if (!incomeId) errs.income_id = ["Please select an income / fee type."];
    if (!String(amountPaid).trim()) errs.amount_paid = ["Please enter the amount paid."];
    else if (Number.isNaN(Number(amountPaid)) || Number(amountPaid) <= 0)
      errs.amount_paid = ["Enter a valid amount greater than 0."];
    if (!paidDate) errs.paid_date = ["Please select the payment date."];
    else if (!isNotFutureDate(paidDate)) errs.paid_date = ["Payment date cannot be in the future."];
    if (!slipReference.trim()) errs.slip_reference = ["Please enter the slip / reference number."];
    const receiptErr = validateReceiptFile(receiptFile);
    if (receiptErr) errs.receipt = [receiptErr];
    return errs;
  };

  // Advances to the next step — blocked until every field on the current
  // step passes its validator.
  const goToCoaches = () => {
    const errs = validateClubStep();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStep("coaches");
  };
  const goToPayment = () => {
    const errs = validateCoachesStep();
    setCoachErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStep("payment");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    // A stray Enter keypress in an input submits the form even though the
    // visible action is "Next" — treat that the same as clicking Next
    // instead of firing the real registration request early.
    if (step === "club") {
      goToCoaches();
      return;
    }
    if (step === "coaches") {
      goToPayment();
      return;
    }

    const paymentErrs = validatePaymentStep();
    if (Object.keys(paymentErrs).length > 0) {
      setFieldErrors(paymentErrs);
      return;
    }

    setError("");
    setFieldErrors({});
    setCoachErrors({});
    setSaving(true);

    try {
      const formData = new FormData();
      formData.append("club_name", clubName);
      formData.append("reg_no", regNo);
      formData.append("reg_year", regYear);
      formData.append("primary_phone_number", primaryPhone);
      formData.append("secondary_phone_number", secondaryPhone);
      formData.append("address", address);
      formData.append("membership_start_date", membershipStart);
      const coachPayload = coaches.map((c, i) => ({
        title: c.title,
        initials: c.initials,
        nameWithInitials: c.nameWithInitials,
        lastName: c.lastName,
        memberGenderId: c.memberGenderId,
        email: c.email,
        nic: c.nic,
        primaryPhone: c.primaryPhone,
        secondaryPhone: c.secondaryPhone || null,
        dob: c.dob || null,
        address: c.address,
        // Exactly one coach (the first) is "primary"; every other coach
        // added via "Add Another Coach" is sent as "secondary". Unlike
        // every other key here, the backend does NOT camelCase-normalize
        // this one — it expects the literal key "coach_role" (confirmed by
        // a 422 on "coaches.1.coach_role" when this was sent as
        // "coachRole"). The value is validated against a fixed whitelist —
        // ClubController::store's rules accept only "primary", "secondary",
        // "Primary Coach", or "Secondary Coach" (see
        // sportscience_backend/app/Http/Controllers/ClubController.php)
        // — "other" is not one of them.
        coach_role: i === 0 ? "primary" : "secondary",
      }));
      formData.append("coaches", JSON.stringify(coachPayload));
      // Photos travel as separate multipart entries keyed by coach index
      // (not embedded in the JSON coaches array) — same coach_photos[i]
      // convention the public /club-registrations flow uses, so a coach
      // between two photographed ones can be left without one.
      coaches.forEach((c, i) => {
        if (c.photo) formData.append(`coach_photos[${i}]`, c.photo);
      });
      // Not yet persisted server-side (see the NOTE on the payment-step
      // fields above) — sent regardless so this keeps working once the
      // backend catches up.
      formData.append("income_id", incomeId);
      formData.append("amount_paid", amountPaid);
      formData.append("paid_date", paidDate);
      formData.append("slip_reference", slipReference);
      if (receiptFile) formData.append("receipt", receiptFile);
      if (note) formData.append("note", note);

      const created = await createClubLegacy(formData);
      toast.success("Club registered.");
      onSaved(created);
    } catch (err) {
      const info = errInfo(err, "Could not register the club.");
      setError(info.message);
      // Split dot-path coach errors — coaches.{index}.{field} for the JSON
      // payload fields, coach_photos.{index} for the separately-keyed
      // photo files — out from club-level ones so each lands next to the
      // right field.
      const topLevel = {};
      const perCoach = {};
      Object.entries(info.fields).forEach(([key, msgs]) => {
        const m = key.match(/^coaches\.(\d+)\.(.+)$/) || key.match(/^coach_photos\.(\d+)$/);
        if (m) {
          const coach = coaches[Number(m[1])];
          const field = m[2] || "photo";
          if (coach) perCoach[coach.id] = { ...(perCoach[coach.id] || {}), [field]: msgs?.[0] };
        } else {
          topLevel[key] = msgs;
        }
      });
      setFieldErrors(topLevel);
      setCoachErrors(perCoach);
      // Field errors only render on the step that owns them — jump back to
      // whichever earlier step actually has one so the user sees it instead
      // of just the generic message on "payment". Club-level errors (e.g. a
      // duplicate registration number) take priority since they're first;
      // a payment-step field (income_id, amount_paid, ...) needs no jump —
      // "payment" is already the step the form was submitted from.
      const hasClubError = Object.keys(topLevel).some((k) => CLUB_STEP_FIELDS.has(k));
      if (hasClubError) setStep("club");
      else if (Object.keys(perCoach).length > 0) setStep("coaches");
      setSaving(false);
    }
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit} className="space-y-4">
      {step === "club" ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>Club name</label>
              <input
                className={fieldClass("club_name")}
                value={clubName}
                onChange={(e) => {
                  setClubName(e.target.value);
                  clearFieldError("club_name");
                }}
                required
              />
              {fieldError("club_name") && <p className="text-xs text-red-600 mt-1">{fieldError("club_name")}</p>}
            </div>

            <div>
              <label className={labelClass}>Registration number</label>
              <input
                className={fieldClass("reg_no")}
                value={regNo}
                onChange={(e) => {
                  setRegNo(e.target.value);
                  clearFieldError("reg_no");
                }}
                required
              />
              {fieldError("reg_no") && <p className="text-xs text-red-600 mt-1">{fieldError("reg_no")}</p>}
            </div>

            <div>
              <label className={labelClass}>Registration year</label>
              <input
                type="number"
                min="1900"
                max={new Date().getFullYear()}
                className={fieldClass("reg_year")}
                value={regYear}
                onChange={(e) => {
                  setRegYear(e.target.value);
                  clearFieldError("reg_year");
                }}
                required
              />
              {fieldError("reg_year") && <p className="text-xs text-red-600 mt-1">{fieldError("reg_year")}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Primary phone</label>
              <input
                className={fieldClass("primary_phone_number")}
                value={primaryPhone}
                onChange={(e) => {
                  setPrimaryPhone(e.target.value);
                  clearFieldError("primary_phone_number");
                }}
                required
              />
              {fieldError("primary_phone_number") && (
                <p className="text-xs text-red-600 mt-1">{fieldError("primary_phone_number")}</p>
              )}
            </div>
            <div>
              <label className={labelClass}>Secondary phone</label>
              <input
                className={fieldClass("secondary_phone_number")}
                value={secondaryPhone}
                onChange={(e) => {
                  setSecondaryPhone(e.target.value);
                  clearFieldError("secondary_phone_number");
                }}
              />
              {fieldError("secondary_phone_number") && (
                <p className="text-xs text-red-600 mt-1">{fieldError("secondary_phone_number")}</p>
              )}
            </div>
          </div>

          <div>
            <label className={labelClass}>Address</label>
            <textarea
              className={`${fieldClass("address")} resize-none`}
              rows={2}
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                clearFieldError("address");
              }}
              required
            />
            {fieldError("address") && <p className="text-xs text-red-600 mt-1">{fieldError("address")}</p>}
          </div>

          <div>
            <label className={labelClass}>Membership start date</label>
            <input
              type="date"
              className={fieldClass("membership_start_date")}
              value={membershipStart}
              onChange={(e) => {
                setMembershipStart(e.target.value);
                clearFieldError("membership_start_date");
              }}
              required
            />
            {fieldError("membership_start_date") && (
              <p className="text-xs text-red-600 mt-1">{fieldError("membership_start_date")}</p>
            )}
          </div>
        </>
      ) : step === "coaches" ? (
        <>
          <CoachFields
            label="Primary Coach"
            coach={coaches[0]}
            genders={genders}
            titles={titles}
            error={coachErrors[coaches[0].id]}
            onChange={(field, value) => handleCoachChange(coaches[0].id, field, value)}
            photoRequired
          />

          {coaches.slice(1).map((c, idx) => (
            <CoachFields
              key={c.id}
              label={`Other Coach ${idx + 1}`}
              coach={c}
              genders={genders}
              titles={titles}
              error={coachErrors[c.id]}
              onChange={(field, value) => handleCoachChange(c.id, field, value)}
              onRemove={() => removeCoach(c.id)}
            />
          ))}

          <button type="button" onClick={addCoach} className={`${btnSecondary} w-full`}>
            + Add Another Coach
          </button>
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Income / fee type</label>
              <select
                className={fieldClass("income_id")}
                value={incomeId}
                onChange={(e) => {
                  const id = e.target.value;
                  setIncomeId(id);
                  clearFieldError("income_id");
                }}
                required
              >
                <option value="" disabled>
                  Select…
                </option>
                {incomes.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.description}
                  </option>
                ))}
              </select>
              {fieldError("income_id") && <p className="text-xs text-red-600 mt-1">{fieldError("income_id")}</p>}
            </div>

            <div>
              <label className={labelClass}>Slip / reference number</label>
              <input
                className={fieldClass("slip_reference")}
                value={slipReference}
                onChange={(e) => {
                  setSlipReference(e.target.value);
                  clearFieldError("slip_reference");
                }}
                required
              />
              {fieldError("slip_reference") && (
                <p className="text-xs text-red-600 mt-1">{fieldError("slip_reference")}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Amount paid</label>
              <input
                type="number"
                step="0.01"
                min="0"
                className={fieldClass("amount_paid")}
                value={amountPaid}
                onChange={(e) => {
                  setAmountPaid(e.target.value);
                  clearFieldError("amount_paid");
                }}
                required
              />
              {fieldError("amount_paid") && <p className="text-xs text-red-600 mt-1">{fieldError("amount_paid")}</p>}
            </div>
            <div>
              <label className={labelClass}>Payment date</label>
              <input
                type="date"
                className={fieldClass("paid_date")}
                value={paidDate}
                onChange={(e) => {
                  setPaidDate(e.target.value);
                  clearFieldError("paid_date");
                }}
                required
              />
              {fieldError("paid_date") && <p className="text-xs text-red-600 mt-1">{fieldError("paid_date")}</p>}
            </div>
          </div>

          <ReceiptFileInput
            file={receiptFile}
            onChange={(f) => {
              setReceiptFile(f);
              clearFieldError("receipt");
            }}
            error={fieldError("receipt")}
          />

          <div>
            <label className={labelClass}>
              Note <span className="text-gray-400 font-normal">(optional)</span>
            </label>
            <textarea
              className={`${fieldClass("note")} resize-none`}
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
            {fieldError("note") && <p className="text-xs text-red-600 mt-1">{fieldError("note")}</p>}
          </div>
        </>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-3 pt-1">
        {step === "club" && (
          <>
            <button type="button" onClick={onCancel} className={`${btnSecondary} flex-1`}>
              Cancel
            </button>
            <button type="button" onClick={goToCoaches} className={`${btnPrimary} flex-1`}>
              Next
            </button>
          </>
        )}
        {step === "coaches" && (
          <>
            <button
              type="button"
              onClick={() => setStep("club")}
              disabled={saving}
              className={`${btnSecondary} flex-1`}
            >
              Back
            </button>
            <button type="button" onClick={goToPayment} className={`${btnPrimary} flex-1`}>
              Next
            </button>
          </>
        )}
        {step === "payment" && (
          <>
            <button
              type="button"
              onClick={() => setStep("coaches")}
              disabled={saving}
              className={`${btnSecondary} flex-1`}
            >
              Back
            </button>
            <button type="submit" disabled={saving} className={`${btnPrimary} flex-1`}>
              {saving ? "Registering…" : "Submit"}
            </button>
          </>
        )}
      </div>
    </form>
  );
}

// Maps ClubRegistrationController's validated field names (the /staff/club-
// registrations 422 `errors` map) onto this form's local state keys. Same
// idea as ClubRegistration.jsx's CLUB_FIELD_MAP/COACH_FIELD_MAP, but the coach
// keys here match CoachFields'/emptyCoach's own naming (e.g. "nic", not
// "nationalId").
const STAFF_CLUB_FIELD_MAP = {
  club_name: "clubName",
  reg_no: "regNo",
  primary_phone_number: "primaryPhone",
  secondary_phone_number: "secondaryPhone",
  address: "address",
};
const STAFF_COACH_FIELD_MAP = {
  title: "title",
  initials: "initials",
  name_denoted_by_initials: "nameWithInitials",
  lastname: "lastName",
  member_gender_id: "memberGenderId",
  email: "email",
  nic: "nic",
  primary_phone: "primaryPhone",
  secondary_phone: "secondaryPhone",
  date_of_birth: "dob",
  address: "address",
};

// ── Staff-desk ("Counter Registration") form ────────────────────────────────
// Registers a brand-new club + coaches via POST /api/staff/club-registrations
// (createStaffClubRegistration) — a PENDING process paid later at the
// counter, unlike AddClubForm above (legacy /api/clubs/legacy, which
// finalizes immediately together with a payment record entered on the same
// form). Reuses CoachFields/emptyCoach and the same client-side validators
// AddClubForm already defines at module scope. Unlike the public
// ClubRegistration.jsx wizard, every coach (including the first/"primary"
// one) types their own email directly — there's no OTP-verified session to
// inherit it from.
function StaffClubRegistrationForm({ genders, titles, onDownloadVoucher, downloadingVoucherId, onDone }) {
  const [clubName, setClubName] = useState("");
  const [regNo, setRegNo] = useState("");
  const [primaryPhone, setPrimaryPhone] = useState("");
  const [secondaryPhone, setSecondaryPhone] = useState("");
  const [address, setAddress] = useState("");
  const [coaches, setCoaches] = useState(() => [emptyCoach()]);
  const [coachErrors, setCoachErrors] = useState({});
  const [step, setStep] = useState("club"); // "club" | "coaches" | "summary"

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [result, setResult] = useState(null);

  // Revoke every coach's preview URL (see handleCoachChange below) on unmount,
  // so a full multi-coach registration doesn't leak blob URLs.
  const coachesRef = React.useRef(coaches);
  coachesRef.current = coaches;
  useEffect(
    () => () => coachesRef.current.forEach((c) => c.photoPreview && URL.revokeObjectURL(c.photoPreview)),
    []
  );

  const fieldError = (key) => {
    const msgs = fieldErrors[key];
    return Array.isArray(msgs) && msgs[0] ? msgs[0] : null;
  };
  const fieldClass = (key) =>
    `w-full border rounded-lg px-3 py-2.5 text-sm text-gray-700 focus:outline-none focus:ring-2 transition ${
      fieldError(key)
        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
        : "border-gray-200 focus:ring-blue-500 focus:border-blue-500"
    }`;
  const clearFieldError = (key) =>
    setFieldErrors((fe) => (fe[key] ? { ...fe, [key]: null } : fe));

  const handleCoachChange = (id, field, value) => {
    setCoaches((prev) =>
      prev.map((c) => {
        if (c.id !== id) return c;
        // A photo also gets an object-URL preview, shown on the review step
        // so staff can catch a wrong file before submitting.
        if (field === "photo") {
          if (c.photoPreview) URL.revokeObjectURL(c.photoPreview);
          return { ...c, photo: value, photoPreview: value ? URL.createObjectURL(value) : null };
        }
        return { ...c, [field]: value };
      })
    );
    setCoachErrors((prev) => (prev[id]?.[field] ? { ...prev, [id]: { ...prev[id], [field]: null } } : prev));
  };
  const addCoach = () => setCoaches((prev) => [...prev, emptyCoach()]);
  const removeCoach = (id) => {
    const removed = coaches.find((c) => c.id === id);
    if (removed?.photoPreview) URL.revokeObjectURL(removed.photoPreview);
    setCoaches((prev) => (prev.length === 1 ? prev : prev.filter((c) => c.id !== id)));
    setCoachErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const validateClubStep = () => {
    const errs = {};
    if (!clubName.trim()) errs.club_name = ["Please enter the club name."];
    if (!regNo.trim()) errs.reg_no = ["Please enter the registration number."];
    if (!primaryPhone.trim()) errs.primary_phone_number = ["Please enter the primary phone."];
    else if (!isValidPhone(primaryPhone))
      errs.primary_phone_number = ["Enter a valid phone number (e.g. 0112345678 or +94112345678)."];
    if (secondaryPhone && !isValidPhone(secondaryPhone))
      errs.secondary_phone_number = ["Enter a valid phone number or leave it empty."];
    if (!address.trim()) errs.address = ["Please enter the address."];
    return errs;
  };

  const validateCoachesStep = () => {
    const errs = {};
    coaches.forEach((c, i) => {
      const e = {};
      if (!c.title) e.title = "Please select a title.";
      if (!c.memberGenderId) e.memberGenderId = "Please select a gender.";
      if (!c.initials.trim()) e.initials = "Please enter the initials.";
      else if (!isValidInitials(c.initials)) e.initials = "Use a dot after each initial, e.g. A.B.";
      if (!c.nameWithInitials.trim()) e.nameWithInitials = "Please enter the name with initials.";
      if (!c.lastName.trim()) e.lastName = "Please enter the last name.";
      if (!c.email.trim()) e.email = "Please enter an email address.";
      else if (!isValidEmail(c.email)) e.email = "Enter a valid email address.";
      if (!c.nic.trim()) e.nic = "Please enter the NIC.";
      else if (!isValidNIC(c.nic)) e.nic = "Enter a valid NIC — 9 digits + V/X (e.g. 200012345V) or 12 digits.";
      if (!c.primaryPhone.trim()) e.primaryPhone = "Please enter the primary phone.";
      else if (!isValidMobilePhone(c.primaryPhone))
        e.primaryPhone = "Enter a valid mobile number (e.g. 0712345678 or +94712345678).";
      if (c.secondaryPhone && !isValidPhone(c.secondaryPhone))
        e.secondaryPhone = "Enter a valid phone number or leave it empty.";
      if (c.dob && !isNotFutureDate(c.dob)) e.dob = "Date of birth cannot be in the future.";
      if (!c.address.trim()) e.address = "Please enter the address.";
      const photoErr = photoFileError(c.photo);
      if (photoErr) e.photo = photoErr;
      else if (i === 0 && !c.photo) e.photo = "A profile photo is required for the primary coach.";
      if (Object.keys(e).length) errs[c.id] = e;
    });
    return errs;
  };

  const goToCoaches = () => {
    const errs = validateClubStep();
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStep("coaches");
  };

  const goToSummary = () => {
    const errs = validateCoachesStep();
    setCoachErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setStep("summary");
  };

  const reset = () => {
    coaches.forEach((c) => c.photoPreview && URL.revokeObjectURL(c.photoPreview));
    setClubName("");
    setRegNo("");
    setPrimaryPhone("");
    setSecondaryPhone("");
    setAddress("");
    setCoaches([emptyCoach()]);
    setCoachErrors({});
    setFieldErrors({});
    setError("");
    setStep("club");
    setResult(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;

    // A stray Enter keypress on an earlier step submits the form even though
    // the visible action is "Next"/"Review" — advance instead of firing the
    // real request early.
    if (step === "club") {
      goToCoaches();
      return;
    }
    if (step === "coaches") {
      goToSummary();
      return;
    }

    setError("");
    setFieldErrors({});
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("club_name", clubName);
      fd.append("reg_no", regNo);
      fd.append("primary_phone_number", primaryPhone);
      if (secondaryPhone) fd.append("secondary_phone_number", secondaryPhone);
      fd.append("address", address);
      const coachPayload = coaches.map((c) => ({
        title: c.title,
        initials: c.initials,
        nameWithInitials: c.nameWithInitials,
        lastName: c.lastName,
        memberGenderId: c.memberGenderId,
        email: c.email,
        nic: c.nic,
        primaryPhone: c.primaryPhone,
        secondaryPhone: c.secondaryPhone || null,
        dob: c.dob || null,
        address: c.address,
      }));
      fd.append("coaches", JSON.stringify(coachPayload));
      coaches.forEach((c, i) => {
        if (c.photo) fd.append(`coach_photos[${i}]`, c.photo);
      });

      const created = await createStaffClubRegistration(fd);
      setResult(created);
      setSaving(false);
    } catch (err) {
      const info = errInfo(err, "Could not create this registration.");
      setError(info.message);
      const topLevel = {};
      const perCoach = {};
      Object.entries(info.fields).forEach(([key, msgs]) => {
        const m = key.match(/^coaches\.(\d+)\.(.+)$/) || key.match(/^coach_photos\.(\d+)$/);
        if (m) {
          const coach = coaches[Number(m[1])];
          const field = m[2] ? STAFF_COACH_FIELD_MAP[m[2]] : "photo";
          if (coach && field) perCoach[coach.id] = { ...(perCoach[coach.id] || {}), [field]: msgs?.[0] };
        } else if (STAFF_CLUB_FIELD_MAP[key]) {
          topLevel[STAFF_CLUB_FIELD_MAP[key]] = msgs;
        } else {
          topLevel[key] = msgs;
        }
      });
      setFieldErrors(topLevel);
      setCoachErrors(perCoach);
      const hasClubError = Object.keys(topLevel).some((k) => Object.values(STAFF_CLUB_FIELD_MAP).includes(k));
      if (hasClubError) setStep("club");
      else if (Object.keys(perCoach).length > 0) setStep("coaches");
      setSaving(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-xl mx-auto text-center py-4">
        <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
          <Icon path={ICONS.check} className="w-7 h-7 text-green-600" width={2} />
        </div>
        <h3 className="text-lg font-bold mb-1" style={{ color: TOKENS.NAVY }}>
          Registration process created
        </h3>
        <p className="text-sm text-gray-500 mb-5">
          Print the voucher below and hand it to the club's primary coach. Once they pay at the counter, reconcile
          it from the "Confirm Payment" tab using this reference.
        </p>
        <div className="bg-gray-50 rounded-xl p-4 mb-5 text-left space-y-1.5">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Payment reference</span>
            <span className="font-mono font-semibold text-gray-800">{result.payment_reference || "—"}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Payable amount</span>
            <span className="font-semibold text-gray-800">{money(result.payable_amount)}</span>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => onDownloadVoucher(result.process_id)}
            disabled={downloadingVoucherId === result.process_id}
            className={`${btnPrimary} flex-1`}
          >
            {downloadingVoucherId === result.process_id ? "Preparing…" : "Download Voucher"}
          </button>
          <button onClick={reset} className={`${btnSecondary} flex-1`}>
            Register Another
          </button>
        </div>
        <button onClick={onDone} className="mt-4 text-sm font-semibold" style={{ color: TOKENS.PRIMARY }}>
          Done
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {step === "club" ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Club name</label>
              <input
                className={fieldClass("clubName")}
                value={clubName}
                onChange={(e) => {
                  setClubName(e.target.value);
                  clearFieldError("clubName");
                }}
                required
              />
              {fieldError("clubName") && <p className="text-xs text-red-600 mt-1">{fieldError("clubName")}</p>}
            </div>
            <div>
              <label className={labelClass}>Registration number</label>
              <input
                className={fieldClass("regNo")}
                value={regNo}
                onChange={(e) => {
                  setRegNo(e.target.value);
                  clearFieldError("regNo");
                }}
                required
              />
              {fieldError("regNo") && <p className="text-xs text-red-600 mt-1">{fieldError("regNo")}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Primary phone</label>
              <input
                className={fieldClass("primaryPhone")}
                value={primaryPhone}
                onChange={(e) => {
                  setPrimaryPhone(e.target.value);
                  clearFieldError("primaryPhone");
                }}
                required
              />
              {fieldError("primaryPhone") && <p className="text-xs text-red-600 mt-1">{fieldError("primaryPhone")}</p>}
            </div>
            <div>
              <label className={labelClass}>Secondary phone</label>
              <input
                className={fieldClass("secondaryPhone")}
                value={secondaryPhone}
                onChange={(e) => {
                  setSecondaryPhone(e.target.value);
                  clearFieldError("secondaryPhone");
                }}
              />
              {fieldError("secondaryPhone") && (
                <p className="text-xs text-red-600 mt-1">{fieldError("secondaryPhone")}</p>
              )}
            </div>
          </div>

          <div>
            <label className={labelClass}>Address</label>
            <textarea
              className={`${fieldClass("address")} resize-none`}
              rows={2}
              value={address}
              onChange={(e) => {
                setAddress(e.target.value);
                clearFieldError("address");
              }}
              required
            />
            {fieldError("address") && <p className="text-xs text-red-600 mt-1">{fieldError("address")}</p>}
          </div>
        </>
      ) : step === "coaches" ? (
        <>
          <CoachFields
            label="Primary Coach"
            coach={coaches[0]}
            genders={genders}
            titles={titles}
            error={coachErrors[coaches[0].id]}
            onChange={(field, value) => handleCoachChange(coaches[0].id, field, value)}
            photoRequired
          />

          {coaches.slice(1).map((c, idx) => (
            <CoachFields
              key={c.id}
              label={`Other Coach ${idx + 1}`}
              coach={c}
              genders={genders}
              titles={titles}
              error={coachErrors[c.id]}
              onChange={(field, value) => handleCoachChange(c.id, field, value)}
              onRemove={() => removeCoach(c.id)}
            />
          ))}

          <button type="button" onClick={addCoach} className={`${btnSecondary} w-full`}>
            + Add Another Coach
          </button>
        </>
      ) : (
        <div className="space-y-3">
          <div className="rounded-xl border border-gray-100 p-4">
            <p className="text-xs font-bold text-blue-700 mb-2">Club Information</p>
            {[
              ["Club name", clubName],
              ["Registration number", regNo],
              ["Primary phone", primaryPhone],
              ["Secondary phone", secondaryPhone || "—"],
              ["Address", address],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-1 border-b border-gray-50 last:border-0 text-sm">
                <span className="text-gray-500">{k}</span>
                <span className="font-medium text-gray-800 text-right max-w-[60%] truncate">{v || "—"}</span>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-gray-100 p-4">
            <p className="text-xs font-bold text-blue-700 mb-2">
              Coach List ({coaches.length} Coach{coaches.length > 1 ? "es" : ""})
            </p>
            {coaches.map((c, i) => (
              <div key={c.id} className="py-3 border-b border-gray-50 last:border-0">
                <div className="flex items-center gap-3 mb-1.5">
                  <Avatar name={[c.title, c.initials, c.lastName].filter(Boolean).join(" ")} url={c.photoPreview} size={40} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {[c.title, c.initials, c.lastName].filter(Boolean).join(" ") || "—"}
                      </p>
                      {i === 0 && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                          Primary Coach
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate">{c.email}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 text-xs text-gray-600 pl-1">
                  <p>NIC: {c.nic || "—"}</p>
                  <p>Phone: {c.primaryPhone || "—"}</p>
                  <p className="sm:col-span-2">Address: {c.address || "—"}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-3 pt-1">
        {step === "club" ? (
          <button type="button" onClick={goToCoaches} className={`${btnPrimary} flex-1`}>
            Next
          </button>
        ) : step === "coaches" ? (
          <>
            <button
              type="button"
              onClick={() => setStep("club")}
              disabled={saving}
              className={`${btnSecondary} flex-1`}
            >
              Back
            </button>
            {/* Distinct keys on Review / Confirm & Register: without them React
                reuses the same <button> DOM node across the step change and
                flips its type to "submit" mid-click, so the click that opens
                the summary also submits it. */}
            <button key="review" type="button" onClick={goToSummary} className={`${btnPrimary} flex-1`}>
              Review
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setStep("coaches")}
              disabled={saving}
              className={`${btnSecondary} flex-1`}
            >
              Back
            </button>
            <button key="confirm" type="submit" disabled={saving} className={`${btnPrimary} flex-1`}>
              {saving ? "Creating…" : "Confirm & Register"}
            </button>
          </>
        )}
      </div>
    </form>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────
// List is editable: PUT/PATCH /api/clubs/{id} (see updateClub() in
// clubService.js) is gated only by "edit_clubs" and works with a regular
// admin/staff bearer token. This is distinct from the singular
// PUT/PATCH /api/club/{id} route, which is coach-self-service-only (gated by
// "primary.coach" middleware) and not used here.
function ClubsManagementList() {
  const { can, user } = useAuth();
  const navigate = useNavigate();
  // Instructor-only UI restriction (frontend-only — no permission change):
  // hides the "Download Voucher" link on the "Pending Registrations" sub-tab
  // for the Instructor role. Case-insensitive against user.roles (slugs),
  // same convention isSuperAdmin uses in AuthContext.
  const isInstructor = (user?.roles || []).some((r) => String(r).toLowerCase() === "instructor");
  // Gates the "Add Club" sub-tab, which registers a club via the legacy
  // admin-created flow (POST /api/clubs/legacy — see createClubLegacy).
  const canCreate = can("create_legacy_clubs");
  // Gates the "Counter Registration" sub-tab — staff-desk registration via
  // POST /api/staff/club-registrations (createStaffClubRegistration), a
  // separate permission/flow from create_legacy_clubs above: this one
  // creates a PENDING process paid later at the counter (voucher + the
  // existing Confirm Payment tab), not a one-step finalize-with-payment.
  const canCreateStaffRegistration = can("create_staff_club_registrations");
  // Gates the "Pending Registrations" sub-tab (club registration processes).
  // Separate permission from "view_club_details" — not seeded onto any role
  // but Super Admin/Director today (see SFMIS-Backend's
  // SuperAdminRoleSeeder). The backend route itself only checks system.user,
  // not this permission, so this is a frontend-only visibility gate.
  const canViewClubRegistrations = can("view_club_registrations");
  // Gates the "Confirm Payment" sub-tab (manual/shroff registration-payment
  // reconciliation). Search needs "view_payments"; the confirm form inside
  // the tab additionally needs "submit_manual_payment_receipts" (the
  // Instructor role has this without having broader payment permissions).
  const canViewPayments = can("view_payments");
  const canConfirmPayments = can("submit_manual_payment_receipts");
  // The "Confirm Payment" tab is also shown to holders of
  // "confirm_shroff_payments" (e.g. the Shroff role) — the backend's
  // */manual-confirm routes accept either permission.
  const canConfirmShroff = can("confirm_shroff_payments");
  const canSeeConfirmTab = canViewPayments || canConfirmShroff;
  const canSubmitConfirm = canConfirmPayments || canConfirmShroff;
  // Gates the "Edit" action on each row below.
  const canEdit = can("edit_clubs");
  // Gates the "Deactivate"/"Reactivate" row actions — separate permission
  // from edit_clubs, seeded onto admin/Director Physical Education/
  // Instructor (see SFMIS-Backend's AddMemberInactiveStatusAndDeactivate
  // PermissionsSeeder).
  const canDeactivate = can("deactivate_clubs");
  const [modal, setModal] = useState(null); // { type: "edit" | "deactivate", club }
  const [reactivatingId, setReactivatingId] = useState(null);

  const [clubs, setClubs] = useState([]);
  const [genders, setGenders] = useState([]);
  const [titles, setTitles] = useState([]);
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  // "list" (browse/search table), "add" (registration form), "pending"
  // (registration processes), "confirm-payment" (manual/shroff payment
  // reconciliation), or "my-submissions" (this instructor's own submitted
  // receipts) — each button switches this tab instead of opening a popup /
  // navigating away, matching the pattern used by the Members page's
  // equivalent tabs.
  const [activeTab, setActiveTab] = useState("list");

  const [pendingProcesses, setPendingProcesses] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(true);
  const [pendingSearch, setPendingSearch] = useState("");
  const [downloadingVoucherId, setDownloadingVoucherId] = useState(null);

  // Clubs whose status is Expired or whose membership_end_date has already
  // passed (GET /api/clubs/expired) — a fixed-eligibility view, no
  // date-range picker, matching the backend's own "as of today" definition.
  const [expiredClubs, setExpiredClubs] = useState([]);
  const [expiredAsOf, setExpiredAsOf] = useState(null);
  const [expiredLoading, setExpiredLoading] = useState(true);
  const [expiredSearch, setExpiredSearch] = useState("");

  // Open a blank tab synchronously (inside the click handler) so the browser
  // doesn't treat it as a popup — navigating it to the blob URL after the
  // `await` below would otherwise get blocked in most browsers.
  const handleDownloadVoucher = async (processId) => {
    if (downloadingVoucherId) return;
    const tab = window.open("", "_blank");
    setDownloadingVoucherId(processId);
    try {
      const blob = await getClubShroffVoucherPdf(processId);
      const url = URL.createObjectURL(blob);
      if (tab && !tab.closed) tab.location.href = url;
      else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      tab?.close();
      toast.error(errInfo(err, "Could not download the voucher.").message);
    } finally {
      setDownloadingVoucherId(null);
    }
  };

  const handleReactivate = async (club) => {
    if (reactivatingId) return;
    if (!window.confirm(`Reactivate ${club.club_name}? The club will be marked Active again.`)) return;
    setReactivatingId(club.id);
    try {
      const r = await reactivateClub(club.id);
      toast.success(r?.message || "Club reactivated.");
      fetchClubs();
    } catch (err) {
      toast.error(errInfo(err, "Could not reactivate this club.").message);
    } finally {
      setReactivatingId(null);
    }
  };

  const fetchClubs = useCallback(() => {
    setLoading(true);
    const params = {};
    if (search.trim()) params.search = search.trim();
    listClubs(params)
      .then(setClubs)
      .catch((err) => toast.error(err?.response?.data?.message || "Could not load clubs."))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => {
    const t = setTimeout(fetchClubs, 300);
    return () => clearTimeout(t);
  }, [fetchClubs]);

  // Every club registration attempt (pending, paid-but-unfinalized,
  // completed) — not just the finalized rows listClubs() returns. Only
  // fetched while the tab is actually open.
  const fetchPendingProcesses = useCallback(() => {
    setPendingLoading(true);
    const params = {};
    if (pendingSearch.trim()) params.search = pendingSearch.trim();
    listClubRegistrationProcesses(params)
      .then(setPendingProcesses)
      .catch((err) => toast.error(errInfo(err, "Could not load club registration processes.").message))
      .finally(() => setPendingLoading(false));
  }, [pendingSearch]);

  useEffect(() => {
    if (activeTab !== "pending") return;
    const t = setTimeout(fetchPendingProcesses, 300);
    return () => clearTimeout(t);
  }, [activeTab, fetchPendingProcesses]);

  // Only fetched while the "Expired Clubs" tab is actually open, same
  // lazy-load pattern as fetchPendingProcesses above.
  const fetchExpiredClubs = useCallback(() => {
    setExpiredLoading(true);
    const params = {};
    if (expiredSearch.trim()) params.search = expiredSearch.trim();
    listExpiredClubs(params)
      .then(({ as_of, clubs: rows }) => {
        setExpiredAsOf(as_of);
        setExpiredClubs(rows);
      })
      .catch((err) => toast.error(errInfo(err, "Could not load expired clubs.").message))
      .finally(() => setExpiredLoading(false));
  }, [expiredSearch]);

  useEffect(() => {
    if (activeTab !== "expired") return;
    const t = setTimeout(fetchExpiredClubs, 300);
    return () => clearTimeout(t);
  }, [activeTab, fetchExpiredClubs]);

  useEffect(() => {
    listMemberGenders().then(setGenders).catch(() => {});
    listMemberTitles().then(setTitles).catch(() => {});
    listFees().then(setIncomes).catch(() => {});
  }, []);

  const onSaved = () => {
    setActiveTab("list");
    fetchClubs();
  };

  // These three lists come back as plain (search-filtered but unpaginated)
  // arrays from the backend — client-side pagination only, reset to page 1
  // whenever that tab's own search text changes.
  const clubsPagination = usePagination(clubs, { pageSize: DEFAULT_PAGE_SIZE, resetKey: search });
  const pendingPagination = usePagination(pendingProcesses, { pageSize: DEFAULT_PAGE_SIZE, resetKey: pendingSearch });
  const expiredPagination = usePagination(expiredClubs, { pageSize: DEFAULT_PAGE_SIZE, resetKey: expiredSearch });

  return (
    <div>
      {/* Header */}
      <div className="mb-5">
        <h1 className="text-xl font-bold" style={{ color: TOKENS.NAVY }}>
          Clubs
        </h1>
        <p className="text-sm text-gray-500">Browse registered clubs and their membership.</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 mb-5 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("list")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
            activeTab === "list" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
          style={activeTab === "list" ? { color: TOKENS.PRIMARY } : undefined}
        >
          All Clubs
        </button>
        {canCreate && (
          <button
            onClick={() => setActiveTab("add")}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              activeTab === "add" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === "add" ? { color: TOKENS.PRIMARY } : undefined}
          >
            <Icon path={ICONS.plus} className="w-4 h-4" width={2} />
            Add Club
          </button>
        )}
        {canCreateStaffRegistration && (
          <button
            onClick={() => setActiveTab("staff-register")}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              activeTab === "staff-register" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === "staff-register" ? { color: TOKENS.PRIMARY } : undefined}
          >
            <Icon path={ICONS.plus} className="w-4 h-4" width={2} />
            Counter Registration
          </button>
        )}
        {canViewClubRegistrations && (
          <button
            onClick={() => setActiveTab("pending")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              activeTab === "pending" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === "pending" ? { color: TOKENS.PRIMARY } : undefined}
          >
            Pending Registrations
          </button>
        )}
        <button
          onClick={() => setActiveTab("expired")}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
            activeTab === "expired" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
          style={activeTab === "expired" ? { color: TOKENS.PRIMARY } : undefined}
        >
          Expired Clubs
        </button>
        {canSeeConfirmTab && (
          <button
            onClick={() => setActiveTab("confirm-payment")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              activeTab === "confirm-payment" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === "confirm-payment" ? { color: TOKENS.PRIMARY } : undefined}
          >
            Confirm Payment
          </button>
        )}
        {canConfirmPayments && (
          <button
            onClick={() => setActiveTab("my-submissions")}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition ${
              activeTab === "my-submissions" ? "border-current" : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
            style={activeTab === "my-submissions" ? { color: TOKENS.PRIMARY } : undefined}
          >
            My Submissions
          </button>
        )}
      </div>

      {activeTab === "my-submissions" && canConfirmPayments ? (
        <MySubmissionsTab />
      ) : activeTab === "confirm-payment" && canSeeConfirmTab ? (
        <ConfirmShroffPaymentTab canConfirm={canSubmitConfirm} />
      ) : activeTab === "expired" ? (
        <div>
          {/* Search */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
              </span>
              <input
                className={`${inputClass} pl-9`}
                placeholder="Search by club name, reg no, or primary coach…"
                value={expiredSearch}
                onChange={(e) => setExpiredSearch(e.target.value)}
              />
            </div>
            {expiredAsOf && <p className="text-xs text-gray-400 whitespace-nowrap">As of {expiredAsOf}</p>}
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            {expiredLoading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Spinner />
                <p className="text-sm text-gray-400">Loading expired clubs…</p>
              </div>
            ) : expiredClubs.length === 0 ? (
              <EmptyState icon={ICONS.clubs} text="No expired clubs found." />
            ) : (
              <div className="overflow-x-auto -mx-5 px-5">
                <table className="w-full text-left min-w-[760px]">
                  <thead>
                    <tr className="text-xs text-gray-400 border-b border-gray-100">
                      <th className="py-2 pr-3 font-medium">Club</th>
                      <th className="py-2 px-3 font-medium">Club Code</th>
                      <th className="py-2 px-3 font-medium">Primary Coach</th>
                      <th className="py-2 px-3 font-medium">Membership End Date</th>
                      <th className="py-2 px-3 font-medium">Status</th>
                      <th className="py-2 pl-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expiredPagination.pageItems.map((c) => (
                      <tr key={c.id} className="border-b border-gray-50 last:border-0">
                        <td className="py-3 pr-3">
                          <p className="text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
                            {c.club_name}
                          </p>
                        </td>
                        <td className="py-3 px-3 text-sm font-mono text-gray-600 whitespace-nowrap">{c.club_code}</td>
                        <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">
                          {[c.primary_coach_initials, c.primary_coach_lastname].filter(Boolean).join(" ") || "—"}
                        </td>
                        <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{fmtDateTime(c.membership_end_date)}</td>
                        <td className="py-3 px-3">
                          <Pill>{c.club_status}</Pill>
                        </td>
                        <td className="py-3 pl-3">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              onClick={() => navigate(`${c.id}`)}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
                            >
                              <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              page={expiredPagination.page}
              totalPages={expiredPagination.totalPages}
              onPageChange={expiredPagination.setPage}
              className="mt-4"
            />
          </div>
        </div>
      ) : activeTab === "pending" && canViewClubRegistrations ? (
        <div>
          {/* Search */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
              </span>
              <input
                className={`${inputClass} pl-9`}
                placeholder="Search by club name, reg. no, phone or reference…"
                value={pendingSearch}
                onChange={(e) => setPendingSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm p-6">
            {pendingLoading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Spinner />
                <p className="text-sm text-gray-400">Loading club registration processes…</p>
              </div>
            ) : pendingProcesses.length === 0 ? (
              <EmptyState icon={ICONS.clubs} text="No club registration processes found." />
            ) : (
              <div className="overflow-x-auto -mx-6 px-6">
                <table className="w-full text-left min-w-[860px]">
                  <thead>
                    <tr className="text-xs text-gray-400 border-b border-gray-100">
                      <th className="py-3 pr-3 font-medium">Club</th>
                      <th className="py-3 px-3 font-medium">Phone</th>
                      <th className="py-3 px-3 font-medium">Coaches</th>
                      <th className="py-3 px-3 font-medium">Payment</th>
                      <th className="py-3 px-3 font-medium">Process</th>
                      <th className="py-3 px-3 font-medium">Paid</th>
                      <th className="py-3 px-3 font-medium">Date</th>
                      <th className="py-3 pl-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingPagination.pageItems.map((p) => (
                      <tr key={p.process_id} className="border-b border-gray-50 last:border-0">
                        <td className="py-4 pr-3">
                          <p className="text-sm font-semibold truncate" style={{ color: TOKENS.NAVY }}>
                            {p.club_name}
                          </p>
                          <p className="text-xs text-gray-400 truncate">{p.reg_no}</p>
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-600 whitespace-nowrap">{p.primary_phone_number || "—"}</td>
                        <td className="py-4 px-3 text-sm text-gray-600">{p.no_of_coaches ?? "—"}</td>
                        <td className="py-4 px-3">
                          <Pill>{p.payment_status}</Pill>
                        </td>
                        <td className="py-4 px-3">
                          <Pill>{p.process_status}</Pill>
                        </td>
                        <td className="py-4 px-3 text-sm text-gray-600 whitespace-nowrap">{money(p.paid_amount)}</td>
                        <td className="py-4 px-3 text-xs text-gray-500 whitespace-nowrap">{fmtDateTime(p.created_at)}</td>
                        <td className="py-4 pl-3">
                          <div className="flex items-center justify-end gap-3">
                            {!isInstructor && (
                              <button
                                onClick={() => handleDownloadVoucher(p.process_id)}
                                disabled={downloadingVoucherId === p.process_id}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800 disabled:opacity-50"
                              >
                                <Icon path={ICONS.download} className="w-4 h-4" width={1.8} />
                                {downloadingVoucherId === p.process_id ? "Preparing…" : "Download Voucher"}
                              </button>
                            )}
                            <button
                              onClick={() => navigate(`pending-registrations/${p.process_id}`)}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
                            >
                              <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                              View
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              page={pendingPagination.page}
              totalPages={pendingPagination.totalPages}
              onPageChange={pendingPagination.setPage}
              className="mt-4"
            />
          </div>
        </div>
      ) : activeTab === "add" && canCreate ? (
        <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-7 max-w-full">
          <AddClubForm
            genders={genders}
            titles={titles}
            incomes={incomes}
            onCancel={() => setActiveTab("list")}
            onSaved={onSaved}
          />
        </div>
      ) : activeTab === "staff-register" && canCreateStaffRegistration ? (
        <div className="bg-white rounded-2xl shadow-sm p-5 sm:p-7 max-w-full">
          <StaffClubRegistrationForm
            genders={genders}
            titles={titles}
            onDownloadVoucher={handleDownloadVoucher}
            downloadingVoucherId={downloadingVoucherId}
            onDone={() => setActiveTab(canViewClubRegistrations ? "pending" : "list")}
          />
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="relative flex-1 min-w-[200px]">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                <Icon path={ICONS.search} className="w-4 h-4" width={1.8} />
              </span>
              <input
                className={`${inputClass} pl-9`}
                placeholder="Search by name or registration no…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm p-5">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Spinner />
                <p className="text-sm text-gray-400">Loading clubs…</p>
              </div>
            ) : clubs.length === 0 ? (
              <EmptyState icon={ICONS.clubs} text="No clubs found." />
            ) : (
              <div className="overflow-x-auto -mx-5 px-5">
                <table className="w-full text-left min-w-[760px]">
                  <thead>
                    <tr className="text-xs text-gray-400 border-b border-gray-100">
                      <th className="py-2 pr-3 font-medium">Club</th>
                      <th className="py-2 px-3 font-medium">Club Code</th>
                      <th className="py-2 px-3 font-medium">Membership End Date</th>
                      <th className="py-2 px-3 font-medium">Coaches</th>
                      <th className="py-2 px-3 font-medium">Members</th>
                      <th className="py-2 px-3 font-medium">Status</th>
                      <th className="py-2 pl-3 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clubsPagination.pageItems.map((c) => (
                      <tr key={c.id} className="border-b border-gray-50 last:border-0">
                        <td className="py-3 pr-3">
                          <p className="text-sm font-semibold" style={{ color: TOKENS.NAVY }}>
                            {c.club_name}
                          </p>
                        </td>
                        <td className="py-3 px-3 text-sm font-mono text-gray-600 whitespace-nowrap">{c.club_code}</td>
                        <td className="py-3 px-3 text-sm text-gray-600 whitespace-nowrap">{fmtDateTime(c.membership_end_date)}</td>
                        <td className="py-3 px-3 text-sm text-gray-600">{c.no_of_coaches}</td>
                        <td className="py-3 px-3 text-sm text-gray-600">
                          {c.approved_member_count}
                          {c.pending_member_request_count > 0 && (
                            <span className="text-xs text-amber-600 ml-1">(+{c.pending_member_request_count} pending)</span>
                          )}
                        </td>
                        <td className="py-3 px-3">
                          <Pill>{c.club_status}</Pill>
                        </td>
                        <td className="py-3 pl-3">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              onClick={() => navigate(`${c.id}`)}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-gray-800"
                            >
                              <Icon path={ICONS.eye} className="w-4 h-4" width={1.8} />
                              View
                            </button>
                            {canEdit && (
                              <button
                                onClick={() => setModal({ type: "edit", club: c })}
                                className="inline-flex items-center gap-1 text-xs font-semibold"
                                style={{ color: TOKENS.PRIMARY }}
                              >
                                <Icon path={ICONS.edit} className="w-4 h-4" width={1.8} />
                                Edit
                              </button>
                            )}
                            {canDeactivate && c.club_status === "Active" && (
                              <button
                                onClick={() => setModal({ type: "deactivate", club: c })}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-red-500 hover:text-red-700"
                              >
                                <Icon path={ICONS.x} className="w-4 h-4" width={1.8} />
                                Deactivate
                              </button>
                            )}
                            {canDeactivate && c.club_status === "Inactive" && (
                              <button
                                onClick={() => handleReactivate(c)}
                                disabled={reactivatingId === c.id}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 hover:text-green-800 disabled:opacity-50"
                              >
                                <Icon path={ICONS.check} className="w-4 h-4" width={1.8} />
                                {reactivatingId === c.id ? "Reactivating…" : "Reactivate"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              page={clubsPagination.page}
              totalPages={clubsPagination.totalPages}
              onPageChange={clubsPagination.setPage}
              className="mt-4"
            />
          </div>
        </>
      )}

      {modal?.type === "edit" && (
        <Modal title="Edit Club" onClose={() => setModal(null)} size="lg">
          <EditClubForm
            club={modal.club}
            onCancel={() => setModal(null)}
            onSaved={() => {
              setModal(null);
              fetchClubs();
            }}
          />
        </Modal>
      )}

      {modal?.type === "deactivate" && (
        <InactivateClubModal
          club={modal.club}
          onClose={() => setModal(null)}
          onDone={() => {
            setModal(null);
            fetchClubs();
          }}
        />
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
// Owns its own nested routes (list + per-club detail page, plus the
// per-process detail page for the "Pending Registrations" sub-tab) since
// ADMIN_NAV mounts it at "clubs/*".
export default function ClubsManagement() {
  return (
    <Routes>
      <Route index element={<ClubsManagementList />} />
      <Route path=":clubId" element={<ClubDetailPage />} />
      <Route path="pending-registrations/:processId" element={<ClubProcessDetailPage />} />
    </Routes>
  );
}
